import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';

const API = (path, opts = {}) =>
  fetch(path, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    ...opts,
  });

export default function BillingPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const justPaid = params.get('success') === '1';
  const cancelled = params.get('cancelled') === '1';

  useEffect(() => {
    API('/api/stripe/status')
      .then(r => r.json())
      .then(d => { setStatus(d); setLoading(false); })
      .catch(() => setLoading(false));
    API('/api/referrals')
      .then(r => (r.ok ? r.json() : null))
      .then(d => setReferral(d))
      .catch(() => {});
  }, []);

  const copyReferralLink = () => {
    if (!referral?.referralLink) return;
    navigator.clipboard.writeText(referral.referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const openCheckout = async () => {
    setActionLoading(true); setError('');
    try {
      const r = await API('/api/stripe/checkout', { method: 'POST' });
      const d = await r.json();
      if (d.url) window.location.href = d.url;
      else setError(d.error || 'Failed to open checkout');
    } catch { setError('Connection error'); }
    setActionLoading(false);
  };

  const openPortal = async () => {
    setActionLoading(true); setError('');
    try {
      const r = await API('/api/stripe/portal', { method: 'POST' });
      const d = await r.json();
      if (d.url) window.location.href = d.url;
      else setError(d.error || 'Failed to open billing portal');
    } catch { setError('Connection error'); }
    setActionLoading(false);
  };

  const badge = (s) => {
    const map = {
      trialing:  { label: 'Free trial',   bg: '#d1fae5', color: '#065f46' },
      active:    { label: 'Active',        bg: '#d1fae5', color: '#065f46' },
      past_due:  { label: 'Payment due',  bg: '#fef3c7', color: '#92400e' },
      suspended: { label: 'Suspended',    bg: '#fee2e2', color: '#991b1b' },
      cancelled:     { label: 'Cancelled',      bg: '#f3f4f6', color: '#6b7280' },
      complimentary: { label: 'Complimentary', bg: '#ede9fe', color: '#5b21b6' },
    };
    const cfg = map[s] || { label: s, bg: '#f3f4f6', color: '#6b7280' };
    return (
      <span style={{ fontSize: 12, padding: '4px 12px', borderRadius: 12,
        background: cfg.bg, color: cfg.color, fontWeight: 700 }}>
        {cfg.label}
      </span>
    );
  };

  const card = { background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: '28px 32px', marginBottom: 20 };
  const btn = { padding: '10px 24px', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer', border: 'none' };

  return (
    <>
      <Sidebar />
      <div style={{ marginLeft: 64, padding: '32px 5%', maxWidth: 720 }}>
        <h1 style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 32, fontWeight: 800, marginBottom: 8 }}>Billing</h1>
        <p style={{ color: '#718096', marginBottom: 28 }}>Manage your subscription and payment details.</p>

        {justPaid && (
          <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 10, padding: '14px 20px', marginBottom: 20, color: '#065f46', fontWeight: 600 }}>
            ✓ Payment set up successfully — your account is now active.
          </div>
        )}
        {cancelled && (
          <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, padding: '14px 20px', marginBottom: 20, color: '#92400e' }}>
            Checkout cancelled — no payment was taken.
          </div>
        )}
        {error && (
          <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, padding: '14px 20px', marginBottom: 20, color: '#991b1b' }}>
            {error}
          </div>
        )}

        {loading ? (
          <p style={{ color: '#a0aec0' }}>Loading billing status…</p>
        ) : status ? (
          <>
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Subscription status</div>
                  {badge(status.status)}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 28, fontWeight: 800 }}>
                    £{status.seatRatePence != null ? (status.seatRatePence / 100).toFixed(2) : '—'}
                  </div>
                  <div style={{ fontSize: 12, color: '#718096' }}>per seat / month</div>
                  {status.userCount != null && status.monthlyFee != null && (
                    <div style={{ fontSize: 12, color: '#a0aec0', marginTop: 2 }}>
                      {status.userCount} seat{status.userCount !== 1 ? 's' : ''} · £{status.monthlyFee.toFixed(2)}/month total
                    </div>
                  )}
                </div>
              </div>

              {status.onTrial && (
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14 }}>
                  <strong>{status.trialDaysLeft} day{status.trialDaysLeft !== 1 ? 's' : ''}</strong> remaining on your free trial.
                  {status.trialDaysLeft <= 7 && !status.hasPayment && (
                    <span style={{ color: '#dc2626', marginLeft: 8, fontWeight: 600 }}>Set up billing before your trial ends to avoid suspension.</span>
                  )}
                </div>
              )}

              {status.status === 'suspended' && (
                <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14, color: '#991b1b' }}>
                  Your trial has ended and your account is suspended. Add payment details to reactivate.
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {(!status.hasPayment || status.status === 'suspended') && status.status !== 'complimentary' && (
                  <button onClick={openCheckout} disabled={actionLoading}
                    style={{ ...btn, background: '#10b981', color: '#fff' }}>
                    {actionLoading ? 'Opening…' : 'Set up billing →'}
                  </button>
                )}
                {status.hasBillingPortal && status.status !== 'complimentary' && (
                  <button onClick={openPortal} disabled={actionLoading}
                    style={{ ...btn, background: '#f8f9fa', border: '1px solid #E2E8F0', color: '#0e1b2c' }}>
                    {actionLoading ? 'Opening…' : 'Manage billing / cancel'}
                  </button>
                )}
              </div>
              {status.status === 'complimentary' && (
                <p style={{ fontSize: 14, color: '#5b21b6', marginTop: 12 }}>Your account has been granted complimentary access. No payment is required.</p>
              )}
            </div>

            <div style={card}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>How billing works</div>
              <div style={{ fontSize: 14, color: '#4b5a6e', lineHeight: 1.7 }}>
                <p>
                  You're billed <strong>£{status.seatRatePence != null ? (status.seatRatePence / 100).toFixed(2) : '10.00'} per active broker seat</strong> each
                  month{status.isCustomRate ? ' (a custom rate agreed for your account)' : ''}. Introducers are always free.
                </p>
                <p style={{ marginTop: 8 }}>You can add or remove seats at any time — your next invoice adjusts automatically. Cancel any time with no penalty via <em>Manage billing</em> above.</p>
              </div>
            </div>

            {referral && (
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>🤝 Refer a broker, earn credit</div>
                    <p style={{ fontSize: 13, color: '#718096', margin: 0, maxWidth: 420 }}>
                      £2.50 off your bill every month a broker you referred stays an active paying customer —
                      not a one-off. It stops only if they cancel.
                    </p>
                  </div>
                  {referral.monthlyRecurring > 0 && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 24, fontWeight: 800, color: '#059669' }}>
                        -£{referral.monthlyRecurring.toFixed(2)}<span style={{ fontSize: 13, fontWeight: 600 }}>/mo</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#718096' }}>currently earning, ongoing</div>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8f9fa', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                  <code style={{ fontSize: 13, color: '#0e1b2c', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {referral.referralLink}
                  </code>
                  <button onClick={copyReferralLink}
                    style={{ ...btn, padding: '6px 14px', fontSize: 12, background: copied ? '#d1fae5' : '#fff', border: '1px solid #E2E8F0', color: copied ? '#065f46' : '#0e1b2c', flexShrink: 0 }}>
                    {copied ? '✓ Copied' : 'Copy link'}
                  </button>
                </div>

                <div style={{ display: 'flex', gap: 24, fontSize: 13, color: '#4b5a6e', flexWrap: 'wrap' }}>
                  <span><strong>{referral.active}</strong> active {referral.active === 1 ? 'referral' : 'referrals'}</span>
                  {referral.pending > 0 && <span><strong>{referral.pending}</strong> pending (not yet paying)</span>}
                  <span>£{referral.totalEarned.toFixed(2)} earned lifetime</span>
                  {referral.creditBalance > 0 && <span>£{referral.creditBalance.toFixed(2)} banked, applies once you're billing</span>}
                </div>
                {referral.referralsToFreeSeat > 0 && referral.referralsToFreeSeat <= referral.freeSeatThreshold && (
                  <p style={{ fontSize: 12, color: '#a0aec0', marginTop: 10, marginBottom: 0 }}>
                    {referral.referralsToFreeSeat} more active referral{referral.referralsToFreeSeat !== 1 ? 's' : ''} = a free seat every month, for as long as they all stay active.
                  </p>
                )}
              </div>
            )}
          </>
        ) : (
          <p style={{ color: '#a0aec0' }}>Could not load billing information.</p>
        )}
      </div>
    </>
  );
}
